import { Tabs } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';

import CockpitPanelHeader from '../CockpitPanelHeader';
import Panel from '../Panel';
import GroupFormContainer from './group/GroupFormContainer';
import Groups from './group/Groups';
import ProfileFormContainer from './profile/ProfileFormContainer';
import Profiles from './profile/Profiles';
import UserFormContainer from './user/UserFormContainer';
import Users from './user/Users';

export enum UsersManagementItemTypeEnum {
  USER = 'USER',
  PROFILE = 'PROFILE',
  GROUP = 'GROUP',
}

type ItemType = {
  type: UsersManagementItemTypeEnum;
  id: number | string | null;
};

interface UsersManagementContextType {
  editedItem: ItemType | null;
  setEditedItem: (value: ItemType | null) => void;
}

const UsersManagementContext = createContext<UsersManagementContextType | undefined>(undefined);

export const useUsersManagementContext = (): UsersManagementContextType => {
  const context = useContext<UsersManagementContextType | undefined>(UsersManagementContext);
  if (!context) {
    throw new Error('useUsersManagementContext must be used within a UsersManagementContext.Provider');
  }
  return context;
};

export default function UsersManagement() {
  const { formatMessage } = useIntl();

  const canAccessUsers = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_USER));
  const canAccessProfiles = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_PROFILE));
  const canAccessGroups = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_GROUP));

  const [editedItem, setEditedItem] = useState<ItemType | null>(null);

  const getDefaultTab = () => {
    if (canAccessUsers) {
      return UsersManagementItemTypeEnum.USER;
    }
    if (canAccessGroups) {
      return UsersManagementItemTypeEnum.GROUP;
    }
    return UsersManagementItemTypeEnum.PROFILE;
  };

  const [tabSelectedValue, setTabSelectedValue] = useState<UsersManagementItemTypeEnum>(getDefaultTab());

  const _setEditedItem = useCallback((value: ItemType | null) => {
    value && setTabSelectedValue(value.type);
    setEditedItem(value);
  }, []);

  const contextValue = useMemo(() => ({ editedItem, setEditedItem: _setEditedItem }), [_setEditedItem, editedItem]);

  const isEditing = editedItem !== null;

  const getContent = () => {
    if (isEditing) {
      switch (editedItem.type) {
        case UsersManagementItemTypeEnum.USER:
          return <UserFormContainer />;
        case UsersManagementItemTypeEnum.PROFILE:
          return <ProfileFormContainer />;
        case UsersManagementItemTypeEnum.GROUP:
        default:
          return <GroupFormContainer />;
      }
    }
  };

  return (
    <UsersManagementContext value={contextValue}>
      <Panel header={<CockpitPanelHeader label={formatMessage({ id: 'components.sidebar.USERS_MANAGEMENT' })} />}>
        {isEditing ? (
          getContent()
        ) : (
          <Tabs.Root defaultValue={tabSelectedValue} size="md" lazyMount variant="light" userSelect="none">
            <Tabs.List>
              <Tabs.Trigger value={UsersManagementItemTypeEnum.USER} disabled={!canAccessUsers}>
                <FormattedMessage id="components.usersManagement.users.title" />
              </Tabs.Trigger>
              <Tabs.Trigger value={UsersManagementItemTypeEnum.GROUP} disabled={!canAccessGroups}>
                <FormattedMessage id="components.usersManagement.groups.title" />
              </Tabs.Trigger>
              <Tabs.Trigger value={UsersManagementItemTypeEnum.PROFILE} disabled={!canAccessProfiles}>
                <FormattedMessage id="components.usersManagement.profiles.title" />
              </Tabs.Trigger>
            </Tabs.List>
            <Tabs.ContentGroup>
              {canAccessProfiles && (
                <Tabs.Content value={UsersManagementItemTypeEnum.PROFILE}>
                  <Profiles />
                </Tabs.Content>
              )}
              {canAccessGroups && (
                <Tabs.Content value={UsersManagementItemTypeEnum.GROUP}>
                  <Groups />
                </Tabs.Content>
              )}
              {canAccessUsers && (
                <Tabs.Content value={UsersManagementItemTypeEnum.USER}>
                  <Users />
                </Tabs.Content>
              )}
            </Tabs.ContentGroup>
          </Tabs.Root>
        )}
      </Panel>
    </UsersManagementContext>
  );
}
